import { OpenAPIv3_0, OpenAPIv3_1, dereferenceOpenAPIv3_0, dereferenceOpenAPIv3_1 } from '@criteria/openapi'
import { useSyncExternalStore } from 'react'
import { DocumentStore } from './DocumentStore'
import { JSONDocument, JSONDocumentFormat } from './JSONDocument'

export const uriSymbol = Symbol('criteria.uri')

export class OpenAPIDocumentList {
  snapshot: (OpenAPIv3_0<never> | OpenAPIv3_1<never> | undefined)[]
  constructor(readonly documents: OpenAPIDocument[]) {
    documents.forEach((document) => document.reloadContent().catch(console.error))

    this.snapshot = documents.map((document) => document.openAPI)
  }

  subscribe(callback: () => void) {
    const documentChanged = () => {
      this.snapshot = this.documents.map((document) => document.openAPI)
      callback()
    }
    this.documents.forEach((document) => document.addListener(documentChanged))
    return () => this.documents.forEach((document) => document.removeListener(documentChanged))
  }

  listeners = new Set()
}

export function useOpenAPIDocumentListOpenAPI(
  documentList: OpenAPIDocumentList
): (OpenAPIv3_0<never> | OpenAPIv3_1<never> | undefined)[] {
  return useSyncExternalStore(
    (callback) => documentList.subscribe(callback),
    () => {
      return documentList.snapshot
    }
  )
}

export function useOpenAPIDocumentOpenAPI(document: OpenAPIDocument) {
  return useSyncExternalStore(
    (callback) => {
      document.addListener(callback)
      return () => document.removeListener(callback)
    },
    () => {
      document.reloadContent().catch(console.error)
      return document.openAPI
    }
  )
}

export function useOpenAPIDocumentOpenAPIWarnings(document: OpenAPIDocument) {
  return useSyncExternalStore(
    (onStoreChange) => {
      document.addListener(onStoreChange)
      return () => document.removeListener(onStoreChange)
    },
    () => document.openAPIWarnings
  )
}

export class OpenAPIDocument extends JSONDocument {
  constructor(store: DocumentStore, path: string, fileHandle: FileSystemFileHandle, format: JSONDocumentFormat) {
    super(store, path, fileHandle, format)
  }

  openAPI: OpenAPIv3_0<never> | OpenAPIv3_1<never> | undefined
  openAPIError: Error | undefined
  openAPIWarnings: { message: string }[] = []

  override async updateText(text: string) {
    this.openAPI = undefined
    this.openAPIError = undefined
    this.openAPIWarnings = []

    await super.updateText(text)
  }

  override error(): Error | undefined {
    return this.openAPIError ?? super.error()
  }

  async updateOpenAPI(openAPI: OpenAPIv3_0<never> | OpenAPIv3_1<never>) {
    // TODO: serialize this properly to original files
    await this.updateData(openAPI)
  }

  async reloadContent() {
    if (this.openAPI !== undefined || this.error() !== undefined) {
      return
    }

    await super.reloadContent()
    if (this.data === undefined) {
      return
    }

    this.store.removeAllDependencies(this)

    if (typeof this.data !== 'object' || this.data === null) {
      this.openAPI = undefined
      this.openAPIWarnings = []
      this.openAPIError = new Error(`The document is not a valid ${this.format.toUpperCase()} object.`)
      this.dispatchChange()
      return
    }

    const warnings: { message: string }[] = []

    const retrieve = async (uri: string) => {
      if (!uri.startsWith('playground://')) {
        warnings.push({ message: `Unable to retrieve document at '${uri}'` })
        return {}
      }

      const referencedPath = uri.slice('playground://'.length)
      if (referencedPath === this.path) {
        const data = { ...this.data }
        data[uriSymbol] = referencedPath
        return data
      }

      this.store.addDependency(this, referencedPath)

      let referencedDocument = await this.store.getDocuments()[referencedPath]
      if (!referencedDocument || !(referencedDocument instanceof JSONDocument)) {
        warnings.push({ message: `Unable to retrieve document at '${referencedPath}'` })
        return {}
      }

      await referencedDocument.reloadContent()
      const referencedDocumentError = referencedDocument.jsonError ?? referencedDocument.fileError
      if (referencedDocumentError) {
        warnings.push(referencedDocumentError)
        return {}
      }

      const data = { ...referencedDocument.data }
      if (typeof data === 'object' && data !== null) {
        data[uriSymbol] = referencedPath
      }
      return data
    }

    const dereferenceOptions = {
      baseURI: 'playground://' + this.path,
      retrieve
    }

    try {
      // TODO: Support Swagger 2.0
      const openAPIVersion = 'openapi' in this.data && typeof this.data.openapi === 'string' ? this.data.openapi : null
      if (openAPIVersion && this.data.openapi.match(/3\.1\.\d+/)) {
        this.openAPI = await dereferenceOpenAPIv3_1(this.data as OpenAPIv3_1, dereferenceOptions)
        this.openAPIWarnings = warnings
        this.openAPIError = undefined
      } else if (openAPIVersion && this.data.openapi.match(/3\.0\.(0|1|2|3)/)) {
        this.openAPI = await dereferenceOpenAPIv3_0(this.data as OpenAPIv3_0, dereferenceOptions)
        this.openAPIWarnings = warnings
        this.openAPIError = undefined
      } else {
        // default to 3.1.0
        this.openAPI = await dereferenceOpenAPIv3_1(this.data as OpenAPIv3_1, dereferenceOptions)
        this.openAPIWarnings = warnings
        this.openAPIError = undefined
      }
      if (typeof this.openAPI === 'object' && this.openAPI !== null) {
        ;(this.openAPI as any)[uriSymbol] = this.path
      }
    } catch (error) {
      if (error instanceof Error) {
        this.openAPI = undefined
        this.openAPIWarnings = []
        this.openAPIError = error
      } else {
        this.openAPI = undefined
        this.openAPIWarnings = []
        this.openAPIError = new Error('Unable to dereference OpenAPI document', { cause: error })
      }
    }

    this.dispatchChange()
  }

  onDependencyChange() {
    this.openAPI = undefined
    this.openAPIWarnings = []
    this.openAPIError = undefined

    this.reloadContent()
  }

  static isOpenAPIData(data: any) {
    return typeof data === 'object' && data !== null && 'openapi' in data
  }

  static makeOpenAPIDocument(document: JSONDocument) {
    if (document instanceof OpenAPIDocument) {
      return
    }
    Object.setPrototypeOf(document, OpenAPIDocument.prototype)
    Object.assign(document, { openAPI: undefined, openAPIWarnings: [] } as Pick<
      OpenAPIDocument,
      'openAPI' | 'openAPIWarnings'
    >)
    document.dispatchChange()
  }
}
